import { Theme } from '@mui/material'
import { DefaultParamType, TFnType } from '@tolgee/react'
import { isNil, isNumber, round } from 'lodash'

import { getRevenueDetailsStatColumns, getStatColumns } from '@/constants'
import {
  DashboardIntervalStatFragment,
  DashboardStatWithPreviousFragment,
  GetPosRevenueAndFuelStatsQuery,
  PlanType,
} from '@/graphql'
import { DashboardStat } from '@/mocks/mockSummaryStats'
import { DAIOverTimeLineChartSeries } from '@/pages/charts/ApexCharts/DAIOverTimeLineChart'

import { DateHelpers } from './date.helpers'

const roundValue = (value: number | undefined | null) => {
  return isNumber(value) && value !== undefined ? round(value, 3) : 0
}

const transformStats = (
  data: DashboardStatWithPreviousFragment | undefined,
  t: TFnType<DefaultParamType, string, any>,
  startDate?: string,
  endDate?: string,
): DashboardStat[] => {
  const statColumns = getStatColumns(t)
  const changeTooltipYearOverYear =
    startDate && endDate
      ? DateHelpers.yearOverYearTooltipText(startDate, endDate, t)
      : undefined
  const changeTooltipPreviousPeriod =
    startDate && endDate
      ? DateHelpers.previousPeriodTooltipText(startDate, endDate, t)
      : undefined
  return [
    {
      label: statColumns.TOTAL_REVENUE,
      valueType: 'CURRENCY',
      value: data?.totalRevenue,
      previousValue: data?.previousTotalRevenue,
      name: 'rev',
      requiredPlan: PlanType.PREMIUM,
      changeTooltip: changeTooltipYearOverYear,
      changeTitle: t('statCard.yearOverYear'),
    },
    {
      label: statColumns.EMPTY_FACINGS,
      valueType: 'SINGLE_VALUE',
      value: data?.emptyFacings,
      previousValue: data?.previousEmptyFacings,
      name: 'empty',
      changeTitle: t('statCard.previousPeriod'),
      changeTooltip: changeTooltipPreviousPeriod,
    },
    {
      label: statColumns.COMPLIANCE,
      valueType: 'PERCENTAGE',
      value: roundValue(data?.compliance),
      previousValue: data?.previousCompliance,
      name: 'compliance',
      changeTitle: t('statCard.previousPeriod'),
      changeTooltip: changeTooltipPreviousPeriod,
    },
    {
      label: statColumns.RESTOCK_COMPLETION,
      valueType: 'PERCENTAGE',
      value: roundValue(data?.restockCompletion),
      previousValue: data?.previousRestockCompletion,
      name: 'restock',
      changeTitle: t('statCard.previousPeriod'),
      changeTooltip: changeTooltipPreviousPeriod,
    },
    {
      label: statColumns.TASK_COMPLETION,
      valueType: 'PERCENTAGE',
      value: roundValue(data?.taskCompletion),
      previousValue: data?.previousTaskCompletion,
      name: 'task',
      changeTitle: t('statCard.previousPeriod'),
      changeTooltip: changeTooltipPreviousPeriod,
    },
  ]
}

const transformRevenueDetailsPage = (
  data:
    | GetPosRevenueAndFuelStatsQuery['posRevenueAndFuelStats']['results'][0]
    | undefined,
  t: TFnType<DefaultParamType, string, any>,
  startDate?: string,
  endDate?: string,
): DashboardStat[] => {
  const statColumns = getRevenueDetailsStatColumns(t)
  const changeTooltip =
    startDate && endDate
      ? DateHelpers.yearOverYearTooltipText(startDate, endDate, t)
      : undefined
  return [
    {
      label: statColumns.TOTAL_REVENUE,
      valueType: 'CURRENCY',
      value: data?.totalRevenue,
      previousValue: data?.previousTotalRevenue,
      name: 'rev',
      changeTooltip,
      changeTitle: t('statCard.yearOverYear'),
    },
    {
      label: statColumns.INSIDE_SALES,
      valueType: 'CURRENCY',
      value: data?.insideSales,
      previousValue: data?.previousInsideSales,
      name: 'inside_sales',
      changeTooltip,
      changeTitle: t('statCard.yearOverYear'),
    },
    {
      label: statColumns.OUTSIDE_SALES,
      valueType: 'CURRENCY',
      value: data?.outsideSales,
      previousValue: data?.previousOutsideSales,
      name: 'outside_sales',
      changeTooltip,
      changeTitle: t('statCard.yearOverYear'),
    },
    {
      label: statColumns.FUEL_VOLUME,
      valueType: 'SINGLE_VALUE',
      value: data?.fuelVolume,
      previousValue: data?.previousFuelVolume,
      name: 'fuel_volume',
      changeTooltip,
      changeTitle: t('statCard.yearOverYear'),
    },
  ]
}

const addSeries = (
  t: TFnType<DefaultParamType, string, any>,
  data: DashboardIntervalStatFragment[],
  series: DAIOverTimeLineChartSeries[],
  statKey: keyof DashboardIntervalStatFragment,
  convertToPercentage: boolean,
  dataType: 'number' | 'currency' | 'percentage',
  color?: string,
) => {
  const filteredValues = data.map(item => item[statKey])
  series.push({
    id: statKey,
    name: t('lineChart.seriesName', { seriesName: statKey }),
    color,
    data: filteredValues.map(val =>
      !isNil(val)
        ? round(
            parseFloat((val as number).toFixed(2)) *
              (convertToPercentage ? 100 : 1),
          )
        : null,
    ),
    dataType,
  })
}

const transformIntervalDataForCharts = (
  t: TFnType<DefaultParamType, string, any>,
  data: DashboardIntervalStatFragment[] | undefined,
  theme: Theme,
  isBasic: boolean,
): DAIOverTimeLineChartSeries[] => {
  if (!data || data.length === 0) {
    return []
  }
  const series: DAIOverTimeLineChartSeries[] = []

  if (!isBasic) {
    addSeries(
      t,
      data,
      series,
      'insideSales',
      false,
      'currency',
      theme.palette.success.main,
    )
  }
  addSeries(
    t,
    data,
    series,
    'emptyFacings',
    false,
    'number',
    theme.palette.error.main,
  )
  addSeries(t, data, series, 'compliance', true, 'percentage')
  addSeries(t, data, series, 'restockCompletion', true, 'percentage')
  addSeries(t, data, series, 'taskCompletion', true, 'percentage')

  return series
}

export const DashboardStatsHelpers = {
  transformStats,
  transformIntervalDataForCharts,
  transformRevenueDetailsPage,
}
